import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import EventFiresStrip from './EventFiresStrip';
import '@xyflow/react/dist/style.css';
import { getCompileStatus } from '../../utils/compileStatus';
import { getUpdatedAtMs } from '../../utils/dateUtils';
import useContextualPopover from '../../hooks/useContextualPopover';
import ContextualPopover from '../../components/ContextualPopover/ContextualPopover';
import {
  Container,
  Typography,
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  AppBar,
  Toolbar,
  IconButton,
  InputBase,
  alpha
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as BackIcon,
  Visibility as PreviewIcon,
  PlayArrow as CompileIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { DWLFSignalBuilder } from '@andywilliams/dwlf-charting';
import useNodeTypes from '../../hooks/useNodeTypes';
import {
  fetchEvent,
  fetchEvents,
  createEvent,
  updateEvent,
  compileEvent,
  fetchHistoricalFires,
  clearCurrentEvent,
  clearSuccessMessage,
  clearErrors,
  clearHistoricalFires
} from './customEventsSlice';
import HistoricalFiresDialog from './HistoricalFiresDialog';
import DesktopBestNotice from '../../components/DesktopBestNotice';

/**
 * Undo common storage/API shapes: JSON string, double-encoded JSON, { data: { visual } }, etc.
 */
const parseStoredCustomEventVisual = (raw) => {
  let v = raw;
  if (v === null || v === undefined) {
    return null;
  }
  for (let i = 0; i < 3; i += 1) {
    if (typeof v !== 'string') {
      break;
    }
    try {
      v = JSON.parse(v);
    } catch {
      return null;
    }
  }
  if (!v || typeof v !== 'object') {
    return null;
  }
  if (v.data && typeof v.data === 'object') {
    const d = v.data;
    const dataHasGraph =
      (d.visual !== undefined && d.visual !== null)
      || (d.nodes !== undefined && d.nodes !== null)
      || (d.entry !== undefined && d.entry !== null);
    if (dataHasGraph) {
      v = d.visual !== undefined && d.visual !== null ? d.visual : d;
    }
  }
  if (v.event && typeof v.event === 'object' && v.event.visual !== undefined && v.event.visual !== null) {
    v = v.event.visual;
  }
  if (
    v.visual
    && typeof v.visual === 'object'
    && v.nodes === undefined
    && v.entry === undefined
    && v.edges === undefined
  ) {
    const inner = v.visual;
    if (inner && (inner.nodes || inner.edges || inner.entry || inner.visual?.entry)) {
      v = inner;
    }
  }
  return v;
};


const buildPreflightPayload = ({ name, description, visual }) => ({
  name: name?.trim() || 'Untitled custom event',
  description: description?.trim() || '',
  version: '1.0.0',
  visual,
  metadata: {
    tags: [],
    timeframe: '1D',
    assets: []
  }
});

const CustomEventBuilderPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { eventId } = useParams();
  const isEditing = Boolean(eventId);

  const {
    events: customEventList = [],
    currentEvent,
    currentEventLoading,
    currentEventError,
    eventsError,
    isCreating,
    isUpdating,
    isCompiling,
    historicalFires,
    historicalFiresLoading,
    historicalFiresError,
    successMessage
  } = useSelector((state) => state.customEvents);

  // The node catalogue is the builder's indicator palette, descriptions, firing
  // timeframes and engine-ignored marking (DWLF-370). While it loads the builder
  // says so; if it fails, the palette says the catalogue is unavailable.
  const { catalogue: nodeTypes, loading: nodeTypesLoading } = useNodeTypes();
  const [eventName, setEventName] = useState('');
  const [eventDescription, setEventDescription] = useState('');
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [compilePromptOpen, setCompilePromptOpen] = useState(false);
  const [historicalFiresDialogOpen, setHistoricalFiresDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [errorSnackbarOpen, setErrorSnackbarOpen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [graphImportError, setGraphImportError] = useState(null);
  const [pendingSaveContext, setPendingSaveContext] = useState(null);
  const [saveFlowPending, setSaveFlowPending] = useState(false);

  const signalBuilderRef = useRef(null);
  const graphLoadTimeoutRef = useRef(null);
  const hasUnsavedChangesRef = useRef(false);
  // Suppresses onStrategyChange → setHasUnsavedChanges(true) for a
  // short window after importing a saved graph. React Flow's internal
  // layout cycles (node measurement, edge recalculation) fire
  // onStrategyChange even though the user hasn't edited anything.
  // The signal builder has its own suppression, but React Flow
  // renders can slip past it after the builder's settlement flag
  // is set. This parent-level guard is belt-and-suspenders.
  const importSuppressUntilRef = useRef(0);
  const isSavingRef = useRef(false);
  const saveFlowPendingRef = useRef(false);
  const lastSavedAtRef = useRef(0);

  // Contextual popover for first compile
  const compilePopover = useContextualPopover('first-compile');
  const compileButtonRef = useRef(null);
  const GRAPH_RELOAD_DELAY_MS = 1000;

  const isOlderThanLastSaved = (entity) => {
    const updatedAtMs = getUpdatedAtMs(entity);
    if (updatedAtMs === null || lastSavedAtRef.current === 0) {
      return false;
    }
    return updatedAtMs <= lastSavedAtRef.current;
  };

  useEffect(() => {
    hasUnsavedChangesRef.current = hasUnsavedChanges;
  }, [hasUnsavedChanges]);

  useEffect(() => {
    lastSavedAtRef.current = 0;
    setGraphImportError(null);
    setPendingSaveContext(null);
    saveFlowPendingRef.current = false;
    setSaveFlowPending(false);
    // Reset unsaved changes when eventId changes — prevents stale state from a
    // previous edit session blocking the graph reload timeout guard.
    setHasUnsavedChanges(false);
  }, [eventId]);

  const importBuilderVisual = useCallback((rawVisual, sourceLabel = 'saved custom event') => {
    const visual = parseStoredCustomEventVisual(rawVisual);
    if ((rawVisual !== null && rawVisual !== undefined) && !visual) {
      setGraphImportError(
        `Could not parse the ${sourceLabel} graph (invalid or corrupted JSON).`
      );
      return false;
    }
    if (!visual) {
      setGraphImportError(
        `This ${sourceLabel} has no saved graph data. If this is an older event, try re-saving it from the API or contact support.`
      );
      return false;
    }
    if (!signalBuilderRef.current) {
      return false;
    }

    const result = signalBuilderRef.current.importStrategy(JSON.stringify(visual));
    if (result?.success) {
      setGraphImportError(null);
      // Suppress onStrategyChange callbacks for 2 seconds after a
      // successful import. React Flow's layout engine fires multiple
      // internal renders (node measurement, edge recalculation, viewport
      // adjustment) that trigger onStrategyChange even though the user
      // hasn't edited anything.
      importSuppressUntilRef.current = Date.now() + 2000;
      return true;
    }

    const message = result?.error
      ? `Could not load the ${sourceLabel} graph: ${result.error}`
      : `Could not load the ${sourceLabel} graph.`;
    setGraphImportError(message);
    return false;
  }, []);

  // Show contextual popover on first visit (when editing and compile button is available)
  useEffect(() => {
    // Destructure to avoid unstable object reference as dependency
    const { show: popoverShow, setAnchorEl: setPopoverAnchor } = compilePopover;
    if (isEditing && !hasUnsavedChanges && compileButtonRef.current && !popoverShow) {
      const timer = setTimeout(() => {
        setPopoverAnchor(compileButtonRef.current);
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [isEditing, hasUnsavedChanges, compilePopover.show, compilePopover.setAnchorEl]);

  useEffect(() => {
    return () => {
      if (graphLoadTimeoutRef.current) {
        clearTimeout(graphLoadTimeoutRef.current);
      }
    };
  }, []);


  useEffect(() => {
    if (location.state?.duplicateFrom) {
      const { duplicateFrom } = location.state;
      setEventName(location.state.name || `${duplicateFrom.name} (Copy)`);
      setEventDescription(duplicateFrom.description || '');

      if (graphLoadTimeoutRef.current) {
        clearTimeout(graphLoadTimeoutRef.current);
      }

      const runDuplicateImportAttempt = (attemptIndex) => {
        if (!signalBuilderRef.current) {
          if (attemptIndex < 20) {
            graphLoadTimeoutRef.current = setTimeout(
              () => runDuplicateImportAttempt(attemptIndex + 1),
              200
            );
          } else {
            setGraphImportError('Graph builder did not become ready in time. Try refreshing the page.');
          }
          return;
        }
        importBuilderVisual(duplicateFrom.visual, 'duplicated custom event');
      };

      // Duplicate flow should always import once; user typing shouldn't cancel the initial visual.
      graphLoadTimeoutRef.current = setTimeout(
        () => runDuplicateImportAttempt(0),
        GRAPH_RELOAD_DELAY_MS
      );
    }
  }, [importBuilderVisual, location.state]);

  useEffect(() => {
    if (isEditing && eventId) {
      dispatch(fetchEvent(eventId));
    }

    dispatch(fetchEvents({ limit: 200 }));

    return () => {
      dispatch(clearCurrentEvent());
    };
  }, [dispatch, isEditing, eventId]);

  useEffect(() => {
    if (currentEvent) {
      if (isOlderThanLastSaved(currentEvent)) {
        return;
      }

      setEventName(currentEvent.name || '');
      setEventDescription(currentEvent.description || '');

      if (graphLoadTimeoutRef.current) {
        clearTimeout(graphLoadTimeoutRef.current);
      }

      const runSavedImportAttempt = (attemptIndex) => {
        if (isSavingRef.current) {
          return;
        }
        if (isOlderThanLastSaved(currentEvent)) {
          return;
        }
        if (hasUnsavedChangesRef.current) {
          return;
        }
        if (!signalBuilderRef.current) {
          if (attemptIndex < 20) {
            graphLoadTimeoutRef.current = setTimeout(
              () => runSavedImportAttempt(attemptIndex + 1),
              200
            );
          } else {
            setGraphImportError('Graph builder did not become ready in time. Try refreshing the page.');
          }
          return;
        }
        importBuilderVisual(currentEvent.visual, 'saved custom event');
      };

      graphLoadTimeoutRef.current = setTimeout(
        () => runSavedImportAttempt(0),
        GRAPH_RELOAD_DELAY_MS
      );
    }
  }, [currentEvent, importBuilderVisual]);

  useEffect(() => {
    if (successMessage) {
      setSnackbarOpen(true);
      if (successMessage.includes('created') || successMessage.includes('updated')) {
        setHasUnsavedChanges(false);
        // Show compile prompt after save
        setCompilePromptOpen(true);
        setTimeout(() => {
          dispatch(clearSuccessMessage());
          setSnackbarOpen(false);
        }, 3000);
      } else if (successMessage.includes('compiled')) {
        // After successful compile, fetch historical fires and show dialog
        const currentEventId = eventId || currentEvent?.eventId;
        if (currentEventId) {
          dispatch(fetchHistoricalFires({ eventId: currentEventId, days: 30 }));
          setHistoricalFiresDialogOpen(true);
        }
        setTimeout(() => {
          dispatch(clearSuccessMessage());
          setSnackbarOpen(false);
        }, 3000);
      } else {
        setTimeout(() => {
          dispatch(clearSuccessMessage());
          setSnackbarOpen(false);
        }, 4000);
      }
    }
  }, [successMessage, dispatch, eventId, currentEvent?.eventId]);

  useEffect(() => {
    if (currentEventError || eventsError) {
      setErrorSnackbarOpen(true);
    }
  }, [currentEventError, eventsError]);

  const persistEventData = useCallback(async (eventData) => {
    isSavingRef.current = true;

    try {
      let savedEvent = null;
      if (isEditing) {
        const updateResult = await dispatch(updateEvent({
          eventId,
          updates: eventData
        })).unwrap();
        // Prefer the full event from the response so lastSavedAtRef is accurate
        savedEvent = updateResult?.event || updateResult;
      } else {
        const result = await dispatch(createEvent(eventData)).unwrap();
        // eventId may be at the top level or nested inside result.event (depending on backend version)
        const newEventId = result?.eventId || result?.event?.eventId;
        savedEvent = result?.event || result;
        if (newEventId) {
          navigate(`/custom-events/${newEventId}/edit`, { replace: true });
        }
      }

      lastSavedAtRef.current = getUpdatedAtMs(savedEvent) || 0;
      setSaveDialogOpen(false);
      setPendingSaveContext(null);
    } finally {
      isSavingRef.current = false;
    }
  }, [dispatch, eventId, isEditing, navigate]);

  const handleBack = () => {
    if (hasUnsavedChanges) {
      const confirmLeave = window.confirm('You have unsaved changes. Are you sure you want to leave?');
      if (!confirmLeave) {
        return;
      }
    }
    navigate('/custom-events');
  };

  const handleSave = async () => {
    if (saveFlowPendingRef.current) {
      return;
    }

    if (!eventName.trim()) {
      alert('Please enter a custom event name');
      return;
    }

    if (!signalBuilderRef.current) {
      alert('Event builder not ready');
      return;
    }

    saveFlowPendingRef.current = true;
    setSaveFlowPending(true);

    try {
      const visual = signalBuilderRef.current.exportStrategy();
      const validationError = validateCustomEventGraph(visual);
      if (validationError) {
        alert(validationError);
        return;
      }

      const eventData = buildPreflightPayload({
        name: eventName,
        description: eventDescription,
        visual
      });

      // Open the description dialog on create, or when the description changed;
      // otherwise save straight away. (Tier/complexity preflight gating was
      // removed — the backend no longer blocks saves on tier.)
      const shouldOpenDialog =
        !isEditing || eventDescription !== (currentEvent?.description || '');

      if (shouldOpenDialog) {
        setPendingSaveContext({ visual });
        setSaveDialogOpen(true);
        saveFlowPendingRef.current = false;
        setSaveFlowPending(false);
        return;
      }

      await persistEventData(eventData);
    } catch (error) {
      if (error?.message) {
        alert(`Unable to save this event: ${error.message}`);
        return;
      }
      console.error('Error saving custom event:', error);
      alert('Error saving custom event. Please try again.');
    } finally {
      saveFlowPendingRef.current = false;
      setSaveFlowPending(false);
    }
  };

  const handleSaveConfirm = async () => {
    if (saveFlowPendingRef.current) {
      return;
    }

    if (!pendingSaveContext?.visual) {
      setSaveDialogOpen(false);
      return;
    }

    saveFlowPendingRef.current = true;
    setSaveFlowPending(true);

    try {
      await persistEventData(buildPreflightPayload({
        name: eventName,
        description: eventDescription,
        visual: pendingSaveContext.visual
      }));
    } catch (error) {
      console.error('Error saving custom event:', error);
      alert('Error saving custom event. Please try again.');
    } finally {
      saveFlowPendingRef.current = false;
      setSaveFlowPending(false);
    }
  };

  const validateCustomEventGraph = (visual) => {
    const nodes = visual?.nodes || [];
    if (!nodes.length) {
      return 'Please add nodes before saving this event.';
    }

    const nodeTypes = nodes.map((node) => node?.data?.nodeType || node?.nodeType).filter(Boolean);
    if (!nodeTypes.includes('fire_event')) {
      return 'Custom events require at least one "Fire Event" node.';
    }

    const hasLogicOrInput = nodeTypes.some((type) => type !== 'fire_event' && type !== 'start');
    if (!hasLogicOrInput) {
      return 'Add at least one logic or indicator node before firing the event.';
    }

    const fireEventIds = nodes
      .filter((node) => (node?.data?.nodeType || node?.nodeType) === 'fire_event')
      .map((node) => node.id);
    const edges = visual?.edges || [];
    const hasIncomingToFireEvent = edges.some((edge) => fireEventIds.includes(edge.target));
    if (!hasIncomingToFireEvent) {
      return 'Connect at least one node into the "Fire Event" output.';
    }

    const customEventRefs = nodes.filter(
      (node) => (node?.data?.nodeType || node?.nodeType) === 'custom_event_reference'
    );
    const missingCustomEvent = customEventRefs.some((node) => !node?.data?.customEventId);
    if (missingCustomEvent) {
      return 'Select a custom event for each "Custom Event" node.';
    }
    if (eventId && customEventRefs.some((node) => node?.data?.customEventId === eventId)) {
      return 'Custom events cannot reference themselves.';
    }

    return null;
  };

  const handleSaveCancel = () => {
    setSaveDialogOpen(false);
    setPendingSaveContext(null);
  };

  const handleEventChange = useCallback((visual) => {
    // Ignore empty-graph notifications from the builder (mount churn, prop-driven
    // re-renders, etc.) — they are not real user edits and must not flip the
    // unsaved-changes flag, which would block the saved-graph import.
    const nodeCount = visual?.nodes?.length ?? 0;
    if (nodeCount === 0) {
      return;
    }
    // During the post-import settlement window, skip the dirty flag — React
    // Flow's layout engine fires onStrategyChange multiple times after import
    // (node measurement, edge recalculation) which would falsely mark the graph
    // as dirty.
    const isImportSettling = Date.now() < importSuppressUntilRef.current;
    if (!isImportSettling) {
      setHasUnsavedChanges(true);
    }
    setGraphImportError(null);
  }, []);

  const handlePreview = () => {
    if (signalBuilderRef.current) {
      const visual = signalBuilderRef.current.exportStrategy();
      console.log('Custom Event Preview:', JSON.stringify(visual, null, 2));
    }
  };

  const handleCompile = () => {
    if (!eventId) {
      alert('Please save the event first before compiling');
      return;
    }
    dispatch(compileEvent(eventId));
  };

  const handleCompilePromptCompile = () => {
    setCompilePromptOpen(false);
    // Get the current eventId - for new events it will be set by the URL after navigation
    const currentEventId = eventId || currentEvent?.eventId;
    if (currentEventId) {
      dispatch(compileEvent(currentEventId));
    }
  };

  const handleCompilePromptLater = () => {
    setCompilePromptOpen(false);
  };

  const handleHistoricalFiresClose = () => {
    setHistoricalFiresDialogOpen(false);
    dispatch(clearHistoricalFires());
  };

  // Determine compile status for warning banners
  const compileStatus = useMemo(() => {
    if (!isEditing || !currentEvent) {
      return null;
    }
    return getCompileStatus(currentEvent, { entityType: 'event' });
  }, [isEditing, currentEvent]);

  if (currentEventLoading) {
    return (
      <Container maxWidth="xl" style={{ padding: '20px' }}>
        <Typography variant="h6">Loading custom event...</Typography>
      </Container>
    );
  }

  if (currentEventError && !currentEvent) {
    return (
      <Container maxWidth="xl" style={{ padding: '20px' }}>
        <Alert severity="error">
          {currentEventError}
        </Alert>
      </Container>
    );
  }

  const pageError = currentEventError || eventsError;
  const saveButtonBaseColor = hasUnsavedChanges ? 'var(--dwlf-warn)' : 'var(--dwlf-accent)';
  const saveButtonHoverColor = hasUnsavedChanges ? '#cc9300' : 'var(--dwlf-text)';

  return (
    <Box sx={{
      height: 'calc(100vh - 64px)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      <AppBar position="static" color="default" elevation={1} sx={{ flexShrink: 0 }}>
        <Toolbar>
          <IconButton
            edge="start"
            onClick={handleBack}
            sx={{ mr: 2 }}
          >
            <BackIcon />
          </IconButton>

          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Typography variant="body2" sx={{ mr: 1, color: 'text.secondary', minWidth: 'auto' }}>
              {isEditing ? 'Edit:' : 'New:'}{hasUnsavedChanges && ' *'}
            </Typography>
            <InputBase
              placeholder="Enter custom event name..."
              value={eventName}
              onChange={(e) => {
                setEventName(e.target.value);
                setHasUnsavedChanges(true);
              }}
              sx={{
                flex: 1,
                maxWidth: '400px',
                fontSize: '1.1rem',
                fontWeight: 500,
                color: 'text.primary',
                backgroundColor: alpha('#000', 0.04),
                borderRadius: 1,
                padding: '8px 12px',
                border: '1px solid transparent',
                '&:hover': {
                  backgroundColor: alpha('#000', 0.08),
                },
                '&:focus': {
                  backgroundColor: 'background.paper',
                  borderColor: 'primary.main',
                  outline: 'none',
                },
                '&::placeholder': {
                  color: 'text.disabled',
                  opacity: 1,
                }
              }}
            />
          </Box>

          <Button
            startIcon={<PreviewIcon />}
            onClick={handlePreview}
            sx={{ mr: 2 }}
          >
            Preview
          </Button>

          {isEditing && (
            <Button
              ref={compileButtonRef}
              startIcon={<CompileIcon />}
              onClick={handleCompile}
              disabled={isCompiling || hasUnsavedChanges}
              sx={{
                mr: 2,
                fontFamily: '"Space Mono", monospace',
                fontSize: '0.75rem',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                borderRadius: 0,
                // Ghost, per the BuildEvent/BuildStrategy boards: Save is the one primary action (DWLF-305).
                backgroundColor: 'transparent',
                border: '1px solid var(--dwlf-ink-20)',
                color: 'var(--dwlf-text)',
                '&:hover': {
                  backgroundColor: 'transparent', borderColor: 'var(--dwlf-accent)', color: 'var(--dwlf-accent)',
                },
                '&:disabled': {
                  backgroundColor: 'var(--dwlf-ink-12)',
                  color: 'var(--dwlf-ink-40)'
                }
              }}
            >
              {isCompiling ? 'Compiling...' : 'Compile'}
            </Button>
          )}

          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saveFlowPending || isCreating || isUpdating || !eventName.trim()}
            sx={{
              fontFamily: '"Space Mono", monospace',
              fontSize: '0.75rem',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              borderRadius: 0,
              backgroundColor: saveButtonBaseColor,
              color: 'var(--dwlf-bg)',
              '&:hover': {
                backgroundColor: saveButtonHoverColor,
              }
            }}
          >
            {isCreating || isUpdating ? 'Saving...' : hasUnsavedChanges ? 'Save Changes' : 'Save Event'}
          </Button>
        </Toolbar>
      </AppBar>

      {graphImportError && (
        <Alert severity="error" sx={{ borderRadius: 0, flexShrink: 0 }}>
          {graphImportError}
        </Alert>
      )}

      {compileStatus && !hasUnsavedChanges && (
        <Alert
          severity="warning"
          icon={<WarningIcon />}
          sx={{
            borderRadius: 0,
            flexShrink: 0,
            '& .MuiAlert-message': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%'
            }
          }}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={<CompileIcon />}
              onClick={handleCompile}
              disabled={isCompiling}
              sx={{
                fontWeight: 600,
                backgroundColor: 'var(--dwlf-ink-20)',
                '&:hover': {
                  backgroundColor: 'var(--dwlf-ink-30)'
                }
              }}
            >
              {isCompiling ? 'Compiling...' : 'Compile Now'}
            </Button>
          }
        >
          {compileStatus.message}
        </Alert>
      )}

      <DesktopBestNotice what="The event builder" />
      <Box sx={{
        flex: 1,
        overflow: 'hidden',
        position: 'relative',
        minHeight: 0
      }}>
        <DWLFSignalBuilder
          ref={signalBuilderRef}
          onStrategyChange={handleEventChange}
          variant="event"
          customEvents={customEventList.filter((event) => event.eventId !== eventId)}
          nodeTypes={nodeTypes}
          nodeTypesLoading={nodeTypesLoading}
          getCustomEventHref={(id) => `/custom-events/${id}/view`}
          style={{
            width: '100%',
            height: '100%'
          }}
        />
      </Box>

      {/* What the saved event has actually done (DWLF-305) */}
      {eventId && <EventFiresStrip eventId={eventId} />}

      <Dialog open={saveDialogOpen} onClose={handleSaveCancel} maxWidth="sm" fullWidth>
        <DialogTitle>
          {isEditing ? `Update "${eventName}"` : `Save "${eventName}"`}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'grid', gap: 2 }}>
            <TextField
              fullWidth
              label="Description (Optional)"
              value={eventDescription}
              onChange={(e) => setEventDescription(e.target.value)}
              multiline
              rows={4}
              placeholder="Describe what this custom event represents..."
              helperText="A good description helps you and others understand this event later"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSaveCancel}>Cancel</Button>
          <Button
            onClick={handleSaveConfirm}
            variant="contained"
            disabled={saveFlowPending || isCreating || isUpdating}
            color="primary"
          >
            {saveFlowPending || isCreating || isUpdating ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Compile Prompt Dialog */}
      <Dialog open={compilePromptOpen} onClose={handleCompilePromptLater} maxWidth="xs" fullWidth>
        <DialogTitle>
          Compile Event?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Your changes have been saved. Would you like to compile this event now to start detecting signals?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCompilePromptLater}>
            Later
          </Button>
          <Button
            onClick={handleCompilePromptCompile}
            variant="contained"
            startIcon={<CompileIcon />}
            disabled={isCompiling}
            sx={{
              background: 'linear-gradient(90deg, #10b981, #059669)',
              '&:hover': {
                background: 'linear-gradient(90deg, #059669, #047857)',
              }
            }}
          >
            {isCompiling ? 'Compiling...' : 'Compile Now'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          sx={{
            backgroundColor: '#4caf50',
            color: 'white',
            fontWeight: 600,
            fontSize: '1rem',
            '& .MuiAlert-icon': {
              color: 'white'
            },
            '& .MuiAlert-message': {
              color: 'white'
            },
            '& .MuiAlert-action': {
              color: 'white'
            }
          }}
          onClose={() => setSnackbarOpen(false)}
        >
          <span style={{ color: 'white' }}>
            ✅ {successMessage}
          </span>
        </Alert>
      </Snackbar>

      <Snackbar
        open={errorSnackbarOpen}
        autoHideDuration={4000}
        onClose={() => {
          setErrorSnackbarOpen(false);
          dispatch(clearErrors());
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity="error"
          onClose={() => {
            setErrorSnackbarOpen(false);
            dispatch(clearErrors());
          }}
          sx={{
            fontWeight: 600,
            fontSize: '1rem'
          }}
        >
          {pageError}
        </Alert>
      </Snackbar>

      {/* Historical Fires Dialog */}
      <HistoricalFiresDialog
        open={historicalFiresDialogOpen}
        onClose={handleHistoricalFiresClose}
        fires={historicalFires}
        loading={historicalFiresLoading}
        error={historicalFiresError}
        eventName={eventName}
      />

      <ContextualPopover
        open={compilePopover.show}
        anchorEl={compilePopover.anchorEl}
        onDismiss={compilePopover.dismiss}
        message="Hit Compile to evaluate your event across historical data and see where it fires."
      />
    </Box>
  );
};

export default CustomEventBuilderPage;
